import React, { useState, useEffect } from 'react'
import Public from '@mui/icons-material/Public'
import ReactCountryFlag from "react-country-flag"
import { useAuth } from "./api/authStore"
import RenewProxyConfirmDialog from './RenewProxyConfirmDialog'
import RefundProxyConfirmDialog from './RefundProxyConfirmDialog'
import moment from 'moment'
import useMobile from './hooks/useMobile'
import LoadingSpinner from './components/LoadingSpinner'

// Компонент тултипа (мобильно-дружественный)
const Tooltip = ({ children, title, onLongPress, isCheckingSpeed }) => {
    const [show, setShow] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [touchTimer, setTouchTimer] = useState(null)
    
    // Определяем, мобильное ли устройство
    React.useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window)
        }
        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])
    
    const handleTouchStart = (e) => {
        if (isMobile) {
            // Короткое касание - показываем тултип
            setShow(true)
            setTimeout(() => setShow(false), 3000)
            
            // Длительное зажатие - запускаем проверку скорости
            const timer = setTimeout(() => {
                if (onLongPress) {
                    console.log('Long press triggered in ProxyDetailsDialog!') // Отладка
                    onLongPress(e)
                }
            }, 600) // 600ms для длительного нажатия
            
            setTouchTimer(timer)
        }
    }
    
    const handleTouchEnd = (e) => {
        if (isMobile && touchTimer) {
            // Если отпустили до истечения 600ms, отменяем длительное нажатие
            clearTimeout(touchTimer)
            setTouchTimer(null)
        }
    }
    
    const handleTouchCancel = (e) => {
        if (isMobile && touchTimer) {
            clearTimeout(touchTimer)
            setTouchTimer(null)
        }
    }
    
    return (
        <div 
            style={{ 
                position: 'relative', 
                display: 'inline-block',
                width: isMobile ? '100%' : 'auto'
            }}
            onMouseEnter={() => !isMobile && setShow(true)}
            onMouseLeave={() => !isMobile && setShow(false)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
        >
            {/* Показываем спиннер только если идет проверка скорости */}
            {isCheckingSpeed ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <LoadingSpinner size={22} />
                </div>
            ) : (
                children
            )}
            {show && !isCheckingSpeed && (
                <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'var(--medium-gray)',
                    color: 'var(--text-light)',
                    padding: '8px 10px',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--text-xs)',
                    whiteSpace: 'nowrap',
                    zIndex: 1000,
                    border: '1px solid var(--light-gray)',
                    boxShadow: 'var(--shadow-md)',
                    marginBottom: '5px'
                }}>
                    {title}
                </div>
            )}
        </div>
    )
}

// Компонент для уведомлений
const CustomSnackbar = ({ open, onClose, message, severity = 'success' }) => {
    if (!open) return null

    const getSeverityStyle = (severity) => {
        switch (severity) {
            case 'error':
                return {
                    background: '#dc3545',
                    color: 'var(--white)',
                    border: '1px solid #dc3545'
                }
            case 'success':
                return {
                    background: '#28a745',
                    color: 'var(--white)',
                    border: '1px solid #28a745'
                }
            default:
                return {
                    background: 'var(--primary-orange)',
                    color: 'var(--white)',
                    border: '1px solid var(--primary-orange)'
                }
        }
    }

    const style = getSeverityStyle(severity)

    return (
        <div style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10001,
            background: style.background,
            color: style.color,
            border: style.border,
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-md) var(--spacing-lg)',
            boxShadow: 'var(--shadow-card)',
            minWidth: '300px',
            textAlign: 'center',
            animation: 'slideDown 0.3s ease-out'
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--spacing-md)'
            }}>
                <span style={{ fontSize: 'var(--text-sm)', fontWeight: '500' }}>
                    {message}
                </span>
                <button
                    onClick={onClose}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--white)',
                        fontSize: 'var(--text-lg)',
                        cursor: 'pointer',
                        padding: '0',
                        lineHeight: '1'
                    }}
                >
                    ✕
                </button>
            </div>
            <style>{`
                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateX(-50%) translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(-50%) translateY(0);
                    }
                }
            `}</style>
        </div>
    )
}

const ProxyDetailsDialog = (props) => {
    const { user, getAccessTokenSilently, setToken, logout } = useAuth()
    const { isMobile } = useMobile()

    const selectedProxy = props.selectedProxy
    const [saveWhitelistError, setSaveWhitelistError] = useState(false)
    const [whitelist, setWhitelist] = useState(null)
    const [buttonStatus, setButtonDisbled] = useState(true)
    const [openSuccessWhitelistUpdate, setOpenSuccessWhitelistUpdate] = useState(false)
    const [openConfirmRenew, setOpenConfirmRenew] = useState(false)
    const [openConfirmRefund, setOpenConfirmRefund] = useState(false)
    const [renewError, setRenewError] = useState(false)
    const [renewSuccess, setRenewSuccess] = useState(false)
    const [refundError, setRefundError] = useState(false)
    const [refundSuccess, setRefundSuccess] = useState(false)
    const [duplicateTicketError, setDuplicateTicketError] = useState(false)
    
    // Состояние для проверки скорости прокси (как в Proxies.js)
    const [isCheckingSpeed, setIsCheckingSpeed] = useState(false)
    const [isLoadingSpeed, setIsLoadingSpeed] = useState(false)
    const [proxySpeed, setProxySpeed] = useState(null)
    const [speedCheckTime, setSpeedCheckTime] = useState(null)
    
    // CSS стили для анимации спиннера
    React.useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }
        `
        document.head.appendChild(style)
        
        return () => {
            document.head.removeChild(style)
        }
    }, [])

    // Автоматическое закрытие успешных уведомлений через 3 секунды
    useEffect(() => {
        if (renewSuccess) {
            const timer = setTimeout(() => {
                setRenewSuccess(false)
            }, 3000)
            
            return () => clearTimeout(timer)
        }
    }, [renewSuccess])

    useEffect(() => {
        if (refundSuccess) {
            const timer = setTimeout(() => {
                setRefundSuccess(false)
            }, 3000)
            
            return () => clearTimeout(timer)
        }
    }, [refundSuccess])

    useEffect(() => {
        if (duplicateTicketError) {
            const timer = setTimeout(() => {
                setDuplicateTicketError(false)
            }, 3000)
            
            return () => clearTimeout(timer)
        }
    }, [duplicateTicketError])

    useEffect(() => {
        if (openSuccessWhitelistUpdate) {
            const timer = setTimeout(() => {
                setOpenSuccessWhitelistUpdate(false)
            }, 3000)
            
            return () => clearTimeout(timer)
        }
    }, [openSuccessWhitelistUpdate])

    // Вычисляем время до истечения
    const currentTime = new Date().getTime()
    const endTime = new Date(selectedProxy?.end).getTime()
    const hoursUntilExpiry = Math.max(0, Math.ceil((endTime - currentTime) / (1000 * 60 * 60)))
    
    // Вычисляем время с момента покупки для refund (60 минут = 1 час)
    const startTime = new Date(selectedProxy?.start).getTime()
    const hoursSincePurchase = Math.floor((currentTime - startTime) / (1000 * 60 * 60))
    const minutesSincePurchase = Math.floor((currentTime - startTime) / (1000 * 60))
    const canRefund = minutesSincePurchase <= 60 // 60 минут после покупки

    // Функция для очистки agent ID от лишних путей
    const cleanAgentId = (agentId) => {
        if (!agentId) return null
        
        // Если agentId содержит полный путь, извлекаем только ID
        if (typeof agentId === 'string' && agentId.includes('/api/agents/')) {
            const match = agentId.match(/\/api\/agents\/([^\/]+)/)
            return match ? match[1] : agentId
        }
        
        return agentId
    }


    // Загрузка данных прокси при открытии диалога
    useEffect(() => {
        const loadProxyData = async () => {
            if (props.open && selectedProxy?.agent) {
                try {
                    setIsLoadingSpeed(true)
                    
                    // Получаем agent ID из selectedProxy.agent
                    const agentId = cleanAgentId(selectedProxy.agent)
                    
                    // Загружаем полную информацию о прокси по agent ID
                    const proxyData = await props.apiClient.setToken(getAccessTokenSilently()).getAgentInfo(agentId)
                    
                    // Если есть данные о скорости, используем их
                    if (proxyData?.speed?.speed != null) {
                        const speed = (proxyData.speed.speed / 1000 / 1000).toFixed(2)
                        setProxySpeed(speed)
                        setSpeedCheckTime(proxyData.speed.checkTime)
                    } else {
                        // Если нет данных о скорости, устанавливаем null
                        setProxySpeed(null)
                    }
                } catch (e) {
                    setProxySpeed(null)
                } finally {
                    setIsLoadingSpeed(false)
                }
            }
        }
        
        loadProxyData()
    }, [props.open, selectedProxy?.agent])

    const handleClose = () => {
        props.setOpenDetails(false)
    }

    const handleSetSaveErrorClose = () => {
        setSaveWhitelistError(false)
    }

    const handleSetOpenSuccessWhitelistUpdate = () => {
        setOpenSuccessWhitelistUpdate(false)
    }

    const handleSetRenewErrorClose = () => {
        setRenewError(false)
    }

    const handleSetRenewSuccessClose = () => {
        setRenewSuccess(false)
    }

    const handleSetRefundSuccessClose = () => {
        setRefundSuccess(false)
    }

    const handleSetRefundErrorClose = () => {
        setRefundError(false)
    }

    const handleSetDuplicateTicketErrorClose = () => {
        setDuplicateTicketError(false)
    }

    // Функция проверки скорости прокси
    const checkSpeed = async () => {
        try {
            setIsCheckingSpeed(true)
            
            const rawAgentId = selectedProxy?.agent || selectedProxy?.agentId
            const agentId = cleanAgentId(rawAgentId)
            
            const response = await props.apiClient.setToken(getAccessTokenSilently()).checkProxySpeed(agentId)
            const speed = (response?.speed != null) ? (response.speed / 1000 / 1000).toFixed(2) : null
            
            setProxySpeed(speed)
            setSpeedCheckTime(response?.checkTime)
        } catch (e) {
            // В случае ошибки показываем "Error"
            setProxySpeed('Error')
        } finally {
            setIsCheckingSpeed(false)
        }
    }

    const handleWhiteListChange = (e) => {
        const regexExpIP = /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/gi

        if (e.target.value == "") {
            setButtonDisbled(true)
            setWhitelist(e.target.value)
            return
        }

        const ipsArr = e.target.value.split(',')
        let checkedCount = 0

        ipsArr.forEach(element => {
            if (!element.match(regexExpIP)) {
                setButtonDisbled(true)
            } else {
                checkedCount++
            }
        })

        if (checkedCount == ipsArr.length) {
            setButtonDisbled(false)
        }

        setWhitelist(e.target.value)
    }

    const handleWhitelistSave = async () => {
        try {
            const response = await props.apiClient.setToken(getAccessTokenSilently()).saveProxyWhitelist(selectedProxy.id, whitelist)

            setOpenSuccessWhitelistUpdate(true)
            props.setReloadActive(true)
        } catch (e) {
            setSaveWhitelistError(true)
        }
    }

    const handleRenewClick = async () => {
        try {
            setOpenConfirmRenew(true)
            props.setOpenDetails(false) // Закрываем основной диалог
        } catch (e) {
        }
    }

    const handleRefundClick = async () => {
        try {
            setOpenConfirmRefund(true)
            props.setOpenDetails(false) // Закрываем основной диалог
        } catch (e) {
        }
    }

    const renewProxy = async () => {
        try {
            const response = await props.apiClient.setToken(getAccessTokenSilently()).renewProxy(selectedProxy.id)

            setRenewSuccess(true)
            props.setReloadActive(true)
            props.setOpenDetails(false)
        } catch (e) {
            setRenewError(true)
        }
    }

    const refundProxy = async (reason) => {
        try {
            const response = await props.apiClient.setToken(getAccessTokenSilently()).createRefundTicket(selectedProxy.id, reason)

            setOpenConfirmRefund(false)
            setRefundSuccess(true)
            props.setOpenDetails(false)
        } catch (e) {
            setOpenConfirmRefund(false)
            
            // Проверяем, является ли это ошибкой дублирующего тикета
            if (e.response && e.response.status === 401) {
                // Это дублирующий тикет - показываем специальное сообщение
                setDuplicateTicketError(true)
            } else {
                setRefundError(true)
            }
        }
    }

    // Получаем полный IP адрес из info
    const getFullIPAddress = () => {
        return selectedProxy?.info?.ip || 'N/A'
    }


    return (
        <>
            {/* Модальное окно */}
            {props.open && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000
                }} onClick={handleClose}>
                    <div style={{
                        background: 'var(--medium-gray)',
                        border: '1px solid var(--light-gray)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--spacing-2xl)',
                        maxWidth: '500px',
                        width: '90%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: 'var(--shadow-card)',
                        position: 'relative'
                    }} onClick={(e) => e.stopPropagation()}>
                        
                        {/* Кнопка закрытия */}
                        <button
                            onClick={handleClose}
                            style={{
                                position: 'absolute',
                                top: 'var(--spacing-md)',
                                right: 'var(--spacing-md)',
                                background: 'none',
                                border: 'none',
                                color: 'var(--text-muted)',
                                fontSize: 'var(--text-xl)',
                                cursor: 'pointer',
                                padding: '4px'
                            }}
                        >
                            ✕
                        </button>

                        {/* Заголовок с флагом и информацией */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--spacing-md)',
                            marginBottom: 'var(--spacing-xl)',
                            paddingTop: 'var(--spacing-lg)'
                        }}>
                            <div style={{
                                width: '40px',
                                height: '30px',
                                borderRadius: 'var(--radius-sm)',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <ReactCountryFlag 
                                    countryCode={selectedProxy?.info?.loc?.cc} 
                                    svg 
                                    style={{ width: '100%', height: '100%' }}
                                />
                            </div>
                            <div>
                                <h2 style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-xl)',
                                    fontWeight: '700',
                                    margin: '0 0 var(--spacing-xs) 0',
                                    background: 'var(--gradient-orange)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text'
                                }}>
                                    Proxy ID: {selectedProxy?.id}
                                </h2>
                                <p style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    margin: '0',
                                    lineHeight: '1.4'
                                }}>
                                    {selectedProxy?.info?.loc?.reg} • {selectedProxy?.info?.loc?.city} • {selectedProxy?.info?.loc?.zip} • {selectedProxy?.info?.loc?.isp}
                                </p>
                            </div>
                        </div>

                        {/* IP адрес прокси */}
                        <div style={{
                            background: 'var(--dark-gray)',
                            border: '1px solid var(--light-gray)',
                            borderRadius: 'var(--radius-md)',
                            padding: 'var(--spacing-lg)',
                            marginBottom: 'var(--spacing-lg)'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-md)',
                                marginBottom: 'var(--spacing-sm)'
                            }}>
                                <h3 style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    color: 'var(--primary-orange)',
                                    fontSize: 'var(--text-lg)',
                                    fontWeight: '600',
                                    margin: '0'
                                }}>
                                    <Public sx={{ fontSize: 22, color: 'var(--primary-orange)' }} />
                                    Proxy IP
                                </h3>
                                <span style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-base)',
                                    fontFamily: 'var(--font-mono)',
                                    fontWeight: '600'
                                }}>
                                    {getFullIPAddress()}
                                </span>
                            </div>
                            <p style={{
                                color: 'var(--text-muted)',
                                fontSize: 'var(--text-sm)',
                                lineHeight: '1.5',
                                margin: '0'
                            }}>
                                This proxy will be rented by you for 24 hours from the time of purchase.
                            </p>
                        </div>

                        {/* Авторизация по username */}
                        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                            <h3 style={{
                                color: 'var(--text-light)',
                                fontSize: 'var(--text-base)',
                                fontWeight: '600',
                                marginBottom: 'var(--spacing-md)'
                            }}>
                                Authorization by Username/Password
                            </h3>
                            
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--spacing-md)',
                                marginBottom: 'var(--spacing-md)'
                            }}>
                                <div>
                                    <p style={{
                                        color: 'var(--text-muted)',
                                        fontSize: 'var(--text-sm)',
                                        margin: '0 0 var(--spacing-xs) 0'
                                    }}>
                                        Socks5 Server:Port
                                    </p>
                                    <p style={{
                                        color: 'var(--text-light)',
                                        fontSize: 'var(--text-sm)',
                                        margin: '0',
                                        fontFamily: 'var(--font-mono)',
                                        wordBreak: 'break-all'
                                    }}>
                                        {selectedProxy?.socks?.ip}:{selectedProxy?.socks?.port}
                                    </p>
                                </div>
                                <div>
                                    <p style={{
                                        color: 'var(--text-muted)',
                                        fontSize: 'var(--text-sm)',
                                        margin: '0 0 var(--spacing-xs) 0'
                                    }}>
                                        Username:Password
                                    </p>
                                    <p style={{
                                        color: 'var(--text-light)',
                                        fontSize: 'var(--text-sm)',
                                        margin: '0',
                                        fontFamily: 'var(--font-mono)',
                                        wordBreak: 'break-all'
                                    }}>
                                        {selectedProxy?.socks?.user}:{selectedProxy?.socks?.pwd}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Авторизация по IP */}
                        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                            <h3 style={{
                                color: 'var(--text-light)',
                                fontSize: 'var(--text-base)',
                                fontWeight: '600',
                                marginBottom: 'var(--spacing-md)'
                            }}>
                                Authorization by IP
                            </h3>
                            
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--spacing-md)',
                                marginBottom: 'var(--spacing-md)'
                            }}>
                                <div>
                                    <p style={{
                                        color: 'var(--text-muted)',
                                        fontSize: 'var(--text-sm)',
                                        margin: '0 0 var(--spacing-xs) 0'
                                    }}>
                                        Socks5 Server:Port
                                    </p>
                                    <p style={{
                                        color: 'var(--text-light)',
                                        fontSize: 'var(--text-sm)',
                                        margin: '0',
                                        fontFamily: 'var(--font-mono)'
                                    }}>
                                        {selectedProxy?.authPort?.node}:{selectedProxy?.authPort?.port}
                                    </p>
                                </div>
                                <div>
                                    <p style={{
                                        color: 'var(--text-muted)',
                                        fontSize: 'var(--text-sm)',
                                        margin: '0 0 var(--spacing-xs) 0'
                                    }}>
                                        Whitelist (e.g. 1.1.1.1,1.1.1.2)
                                    </p>
                                    <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                                        <input
                                            type="text"
                                            value={whitelist || selectedProxy?.authPort?.whitelist || ''}
                                            onChange={handleWhiteListChange}
                                            placeholder="Enter IP addresses"
                                            style={{
                                                flex: 1,
                                                padding: 'var(--spacing-sm)',
                                                background: 'var(--dark-gray)',
                                                border: '1px solid var(--light-gray)',
                                                borderRadius: 'var(--radius-sm)',
                                                color: 'var(--text-light)',
                                                fontSize: 'var(--text-sm)',
                                                fontFamily: 'var(--font-mono)',
                                                minWidth: 0,
                                                maxWidth: '100%'
                                            }}
                                        />
                                        <button
                                            className="btn btn-outline btn-small"
                                            disabled={buttonStatus}
                                            onClick={handleWhitelistSave}
                                            style={{ minWidth: '60px', flexShrink: 0 }}
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Connection String */}
                        <div style={{
                            background: 'var(--dark-gray)',
                            border: '1px solid var(--light-gray)',
                            borderRadius: 'var(--radius-md)',
                            padding: 'var(--spacing-md)',
                            marginBottom: 'var(--spacing-lg)'
                        }}>
                            <h3 style={{
                                color: 'var(--text-light)',
                                fontSize: 'var(--text-base)',
                                fontWeight: '600',
                                marginBottom: 'var(--spacing-sm)'
                            }}>
                                Connection String
                            </h3>
                            <p style={{
                                color: 'var(--text-muted)',
                                fontSize: 'var(--text-xs)',
                                margin: '0',
                                fontFamily: 'var(--font-mono)',
                                wordBreak: 'break-all'
                            }}>
                                socks5h://{selectedProxy?.socks?.user}:{selectedProxy?.socks?.pwd}@{selectedProxy?.socks?.host}:{selectedProxy?.socks?.port}
                            </p>
                        </div>



                        {/* Кнопки действий */}
                        <div style={{
                            display: 'flex',
                            gap: 'var(--spacing-md)',
                            justifyContent: 'center'
                        }}>
                            {/* Кнопка проверки скорости - показываем только на десктопе */}
                            {!isMobile && (
                                <button
                                    onClick={checkSpeed}
                                    className="btn btn-outline"
                                    style={{ 
                                        minWidth: '120px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        opacity: (isCheckingSpeed || isLoadingSpeed) ? 0.7 : 1,
                                        cursor: (isCheckingSpeed || isLoadingSpeed) ? 'not-allowed' : 'pointer'
                                    }}
                                    disabled={isCheckingSpeed || isLoadingSpeed}
                                >
                                    {isCheckingSpeed ? (
                                        <LoadingSpinner size={22} />
                                    ) : isLoadingSpeed ? (
                                        <LoadingSpinner />
                                    ) : (
                                        <Tooltip 
                                            title={speedCheckTime ? moment(speedCheckTime).fromNow() : 'Speed test result'}
                                        >
                                            <span>
                                                {proxySpeed === 'Error' ? 'Error' : proxySpeed != null ? `${proxySpeed} Mbps` : 'Check speed'}
                                            </span>
                                        </Tooltip>
                                    )}
                                </button>
                            )}
                            
                            {/* На мобильных устройствах показываем только результат скорости */}
                            {isMobile && (
                                <button
                                    className="btn btn-outline"
                                    style={{
                                        minWidth: '120px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        opacity: (isCheckingSpeed || isLoadingSpeed) ? 0.7 : 1,
                                        cursor: (isCheckingSpeed || isLoadingSpeed) ? 'not-allowed' : 'pointer'
                                    }}
                                    disabled={isCheckingSpeed || isLoadingSpeed}
                                >
                                    <Tooltip 
                                        title={speedCheckTime ? moment(speedCheckTime).fromNow() : 'Hold to test speed'}
                                        onLongPress={() => {
                                            if (!isCheckingSpeed && !isLoadingSpeed) {
                                                checkSpeed()
                                            }
                                        }}
                                        isCheckingSpeed={isCheckingSpeed}
                                    >
                                        <span>
                                            {isLoadingSpeed ? (
                                                <LoadingSpinner />
                                            ) : isCheckingSpeed ? (
                                                <LoadingSpinner size={22} />
                                            ) : (
                                                proxySpeed === 'Error' ? 'Error' : proxySpeed != null ? `${proxySpeed} Mbps` : 'Check speed'
                                            )}
                                        </span>
                                    </Tooltip>
                                </button>
                            )}
                            <button
                                className={`btn ${canRefund ? 'btn-buy' : 'btn-outline'} btn-small`}
                                onClick={handleRefundClick}
                                disabled={!canRefund}
                                style={{ 
                                    minWidth: '100px',
                                    opacity: canRefund ? 1 : 0.5
                                }}
                            >
                                Refund
                            </button>
                            <button
                                className="btn btn-buy btn-small"
                                onClick={handleRenewClick}
                                style={{ minWidth: '100px' }}
                            >
                                Renew
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* Диалоги подтверждения */}
            <RenewProxyConfirmDialog 
                openConfirmRenew={openConfirmRenew} 
                setOpenConfirmRenew={setOpenConfirmRenew}
                renewProxy={renewProxy}
                selectedProxy={selectedProxy}
            />

            <RefundProxyConfirmDialog 
                openConfirmRefund={openConfirmRefund}
                refundProxy={refundProxy}
                setOpenConfirmRefund={setOpenConfirmRefund}
                selectedProxy={selectedProxy}
            />

            {/* Уведомления */}
            <CustomSnackbar
                open={saveWhitelistError}
                onClose={handleSetSaveErrorClose}
                message="Error saving whitelist."
                severity="error"
            />

            <CustomSnackbar
                open={openSuccessWhitelistUpdate}
                onClose={handleSetOpenSuccessWhitelistUpdate}
                message="Proxy whitelist updated!"
                severity="success"
            />

            <CustomSnackbar
                open={renewError}
                onClose={handleSetRenewErrorClose}
                message="Error renew proxy."
                severity="error"
            />

            <CustomSnackbar
                open={renewSuccess}
                onClose={handleSetRenewSuccessClose}
                message="Proxy renewed successfully!"
                severity="success"
            />

            <CustomSnackbar
                open={refundError}
                onClose={handleSetRefundErrorClose}
                message="Error creating refund ticket."
                severity="error"
            />

            <CustomSnackbar
                open={refundSuccess}
                onClose={handleSetRefundSuccessClose}
                message="Refund ticket created successfully!"
                severity="success"
            />

            <CustomSnackbar
                open={duplicateTicketError}
                onClose={handleSetDuplicateTicketErrorClose}
                message="A refund ticket for this proxy already exists!"
                severity="error"
            />
        </>
    )
}

export default ProxyDetailsDialog